import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, formatApiError } from "../../lib/adminApi";
import ImageUpload from "../../components/ImageUpload";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", logo: "", logo_has_transparency: false, url: "", order: 0 };

export default function AdminAllyForm() {
  const { id } = useParams();
  const isEdit = id && id !== "nuevo";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await adminApi.get(`/allies/${id}`);
        setForm({
          name: data.name || "",
          logo: data.logo || "",
          logo_has_transparency: data.logo_has_transparency || false,
          url: data.url || "",
          order: data.order || 0,
        });
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/admin/aliados");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.logo) return toast.error("El logo es obligatorio.");
    setBusy(true);
    try {
      const payload = { ...form, url: form.url || null };
      if (isEdit) {
        await adminApi.put(`/allies/${id}`, payload);
        toast.success("Aliado actualizado");
      } else {
        await adminApi.post("/allies", payload);
        toast.success("Aliado creado");
      }
      navigate("/admin/aliados");
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="text-[var(--pc-bronze)] animate-spin" />
      </div>
    );
  }

  return (
    <section className="px-10 py-12 max-w-[760px]" data-testid="admin-ally-form">
      <button
        onClick={() => navigate("/admin/aliados")}
        className="flex items-center gap-2 text-[var(--pc-text-soft)] hover:text-[var(--pc-text)] mb-6"
        style={{ fontFamily: "Inter", fontWeight: 400, fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase" }}
        data-testid="back-to-allies"
      >
        <ArrowLeft size={14} /> Volver
      </button>

      <h1
        className="headline-xl text-[var(--pc-text)]"
        style={{ fontSize: "clamp(24px, 2.4vw, 34px)", letterSpacing: "0.16em", lineHeight: 1.2 }}
      >
        {isEdit ? "Editar aliado" : "Nuevo aliado"}
      </h1>
      <div className="h-px w-10 bg-[var(--pc-bronze)] mt-5 opacity-90" />

      <form onSubmit={submit} className="mt-10 space-y-7">
        <div>
          <label className="admin-label">Nombre de la marca</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            className="admin-input w-full"
            required
            data-testid="ally-name"
          />
        </div>

        <ImageUpload
          value={form.logo}
          onChange={(v) => set({ logo: v })}
          onMeta={(meta) => set({ logo_has_transparency: !!meta.has_transparency })}
          label="Logo (cualquier PNG/JPG/AVIF — el fondo blanco/negro se elimina automáticamente)"
          testId="ally-logo"
          removeBg={true}
        />

        <div>
          <label className="admin-label">URL del sitio web (opcional)</label>
          <input
            type="url"
            value={form.url}
            onChange={(e) => set({ url: e.target.value })}
            className="admin-input w-full"
            placeholder="https://..."
            data-testid="ally-url"
          />
        </div>

        <div>
          <label className="admin-label">Orden (menor = aparece primero)</label>
          <input
            type="number"
            value={form.order}
            onChange={(e) => set({ order: parseInt(e.target.value || "0", 10) })}
            className="admin-input w-full"
            data-testid="ally-order"
          />
        </div>

        <div className="pt-4 flex gap-3">
          <button type="submit" disabled={busy} className="admin-btn-primary" data-testid="save-ally">
            {busy ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear aliado"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/aliados")}
            className="admin-btn-ghost"
            data-testid="cancel-ally"
          >
            Cancelar
          </button>
        </div>
      </form>
    </section>
  );
}
